/**
 * Client-side phone helpers for the "Text my agent" SMS opt-in field.
 *
 * These mirror the server's E.164 rule (infra repo `lib/lambda/_shared/cognito.ts`
 * `e164`), which is the source of truth: a valid number is `+` followed by 8–15
 * digits once separators are stripped. We keep the client in step with it so the
 * field never blocks a number the server would accept, nor lets through one it
 * would reject. Ownership is still proven by the OTP round-trip, not by these —
 * they only shape the input and give inline feedback.
 */

/**
 * Seed a US country code when the user starts typing a bare number.
 *
 * If the field was empty and the first thing entered isn't a `+`, assume US and
 * prepend `+1` (so `5551234567` → `+15551234567`). Once the field has content the
 * user edits freely — including deleting the `+1` and typing their own `+44…`; we
 * only intervene on that first keystroke into an empty field.
 */
export function seedPlusOne(prev: string, next: string): string {
  if (prev === '' && next !== '' && !next.startsWith('+')) {
    return `+1${next}`;
  }
  return next;
}

/**
 * True when `value` is a valid E.164 number: a leading `+` and 8–15 digits after
 * stripping display separators (spaces, parens, dashes). Mirrors the server gate
 * `/^\+\d{8,15}$/` applied to the digit-only form.
 */
export function isValidE164(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.startsWith('+')) {
    return false;
  }
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}

/**
 * Pretty-print a US E.164 number (`+1` + 10 digits) as `+1 (XXX) XXX-XXXX` for
 * display. Any other shape (already-formatted, international, invalid) is
 * returned unchanged.
 */
export function formatE164ForDisplay(value: string): string {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value.trim());
  return match ? `+1 (${match[1]}) ${match[2]}-${match[3]}` : value;
}
